import { useRef, useState, useEffect } from "react";
import GradientButton from "../shared/GradientButton";

export default function OtpForm({ onVerify, onResend }) {
    const [otp, setOtp] = useState(Array(6).fill(""));
    const [counter, setCounter] = useState(60);
    const [canResend, setCanResend] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const inputsRef = useRef([]);

    useEffect(() => {
        inputsRef.current[0]?.focus();
    }, []);

    useEffect(() => {
        if (counter <= 0) {
            setCanResend(true);
            return;
        }
        const timer = setInterval(() => setCounter((prev) => prev - 1), 1000);
        return () => clearInterval(timer);
    }, [counter]);

    const handleChange = (e, index) => {
        const value = e.target.value.replace(/\D/, "");
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        setError("");

        if (value && index < 5) inputsRef.current[index + 1].focus();
        if (newOtp.every((d) => d !== "")) verifyOtp(newOtp.join(""));
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputsRef.current[index - 1].focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData("Text").replace(/\D/g, "").slice(0, 6);
        if (!pasteData) return;

        const newOtp = pasteData.split("").concat(Array(6 - pasteData.length).fill(""));
        setOtp(newOtp);
        setError("");

        if (newOtp.every((d) => d !== "")) verifyOtp(newOtp.join(""));

        const firstEmpty = newOtp.findIndex((d) => d === "");
        if (firstEmpty !== -1) inputsRef.current[firstEmpty].focus();
    };

    const verifyOtp = async (otpString) => {
        try {
            setLoading(true);
            setError("");
            await onVerify(otpString);
        } catch (err) {
            setError(err.message || "Invalid OTP");
            setOtp(Array(6).fill(""));
            inputsRef.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setOtp(Array(6).fill(""));
        setCounter(60);
        setCanResend(false);
        setError("");
        inputsRef.current[0]?.focus();
        try {
            setLoading(true);
            await onResend();
        } catch (err) {
            setError(err.message || "Failed to resend OTP");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col items-center w-full p-4">
            <label className="block text-sm mb-2 text-left w-full">Enter the 6-digit code</label>

            <div className="flex gap-2 justify-center mb-2">
                {otp.map((digit, index) => (
                    <input
                        key={index}
                        type="text"
                        maxLength={1}
                        ref={(el) => (inputsRef.current[index] = el)}
                        value={digit}
                        onChange={(e) => handleChange(e, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        onPaste={handlePaste}
                        className="w-12 h-12 text-center text-accent bg-transparent border border-accentLight rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-lg"
                        disabled={loading}
                    />
                ))}
            </div>

            {error && <p className="text-red-400 text-sm mt-2 mb-2">{error}</p>}

            <GradientButton
                onClick={handleResend}
                disabled={!canResend}
                className="w-full py-3 rounded-lg font-semibold hover:bg-white transition mt-5">
                {canResend ? "Resend" : `Resend in (${counter}s)`}
            </GradientButton>
        </div>
    );
}